import { useLayoutEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { isMobile } from 'utils/is-mobile';
import { EAdvancedEventsLayouts } from 'interfaces/sportsbook';
import { ESportsbookLayoutTypes } from 'interfaces/sportsbook-configs';
import { Sport } from 'interfaces/sportsbook-data-levels';
import { EGroupGamesBy } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedEventsLayout/AdvancedMatchCardsDesktop';
import { AdvancedGamesFilteredByCompetitionsSkeleton } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedGamesByCompetitions/Skeleton';
import { GlobalIcon } from 'components/GlobalIcon';
import { NavigationCollapse } from 'components/NavigationCollapse';
import { MatchCardInlineUSASkeleton } from 'components/Shared/MatchCardInlineUSA/__partials/Skeleton';
import { AdvancedGameCardsWithMarketsFilters } from 'newcomponents/Shared/sportsbook/AdvancedMain/AdvancedGameCardsWithMarketsFilters/AdvancedGameCardsWithMarketsFilters';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useSearchParams } from 'hooks/useSearchParams';
import { setAdvancedSportsForMarketsTypes } from 'store/actions/new-sportsbook';
import { getEventIds } from 'store/selectors/betslip';
import { getStreamData } from 'store/selectors/sport-data';

type TAdvancedCompetitionInfo = {
  sportName: string;
  sportAlias: string;
  sportId: number;
  regionAlias: string;
  regionName: string;
  compName: string;
  compId: number;
};

type TGameIdsByType = Record<EAdvancedEventsLayouts, number[]>;

type TFilteredSportsDataType = [TAdvancedCompetitionInfo, TGameIdsByType];

type TGamesFilteredByLeaguesProps = {
  sportsData: Record<string, Sport> | null;
};

export const GamesFilteredByLeagues = ({
  sportsData
}: TGamesFilteredByLeaguesProps) => {
  const dispatch = useDispatch();

  const { coupon } = useSearchParams();
  const { spbLayoutType: layoutType } = useSportsbookConfigs();

  const selectedEventIds = useSelector(getEventIds).map(String);
  const streamData = useSelector(getStreamData);

  const groupedGamesData = useMemo(() => {
    if (sportsData && Object.values(sportsData).filter(item => item)?.length) {
      const resultObj = new Map();
      const sportAliasesSet = new Set<string>();

      sortByOrder(Object.values(sportsData)).forEach(sport => {
        sportAliasesSet.add(sport.alias);
        sport.region &&
          sortByOrder(Object.values(sport.region)).forEach(region => {
            region.competition &&
              sortByOrder(Object.values(region.competition)).forEach(comp => {
                if (comp.game && Object.keys(comp.game).length) {
                  const gameInfo = {
                    sportName: sport.name,
                    sportAlias: sport.alias,
                    sportId: sport.id,
                    regionAlias: region.alias,
                    regionName: region.name,
                    compName: comp.name,
                    compId: comp.id
                  };

                  const games = sortByOrder(
                    Object.values(comp.game),
                    'start_ts'
                  );

                  const prematchGames = games
                    .filter(({ type }) => type !== 1)
                    .map(game => game.id);

                  const gamesByType: {
                    [EAdvancedEventsLayouts.upcoming]?: number[];
                    [EAdvancedEventsLayouts.live]?: number[];
                  } = {};

                  if (prematchGames.length) {
                    gamesByType[EAdvancedEventsLayouts.upcoming] =
                      prematchGames;
                  }

                  if (!coupon) {
                    gamesByType[EAdvancedEventsLayouts.live] = games
                      .filter(({ type }) => type === 1)
                      .map(game => game.id);
                  }

                  resultObj.set(gameInfo, gamesByType);
                }
              });
          });
      });

      const resArr = [];

      for (const keyValue of resultObj.entries()) {
        resArr.push(keyValue);
      }

      return {
        filteredSportsData: resArr as unknown as TFilteredSportsDataType[],
        sportAliasesSet
      };
    }

    return null;
  }, [sportsData, coupon]);

  useLayoutEffect(() => {
    if (groupedGamesData?.sportAliasesSet) {
      groupedGamesData.sportAliasesSet.forEach(sport => {
        dispatch(setAdvancedSportsForMarketsTypes(sport));
      });
    }
  }, [Array.from(groupedGamesData?.sportAliasesSet.values() || []).toString()]);

  useLayoutEffect(() => {
    if (isMobile()) {
      window.scrollTo(0, 0);
    }
  }, []);

  return groupedGamesData ? (
    groupedGamesData.filteredSportsData.map(
      ([compInfo, gamesByType], index) => {
        return (
          !!Object.keys(gamesByType).length && (
            <NavigationCollapse
              key={compInfo.compId}
              defaultActive={index <= 2}
              wrapperClassName="advancedNavigationCollapseItemWrapper"
              headerClassName="advancedNavigationCollapseItemHeader"
              title={
                <>
                  <GlobalIcon
                    lib="flags"
                    name={compInfo.regionAlias}
                    size={24}
                  />
                  {!isMobile() && compInfo.sportName}
                  {!isMobile() && ' | '}
                  {compInfo.regionName}
                  {' | '}
                  {compInfo.compName}
                </>
              }
            >
              {Object.entries(gamesByType).map(([layoutType, gameIds]) => {
                return (
                  !!gameIds.length && (
                    <AdvancedGameCardsWithMarketsFilters
                      key={`${compInfo.compId}${layoutType}`}
                      gameIds={gameIds}
                      selectedSport={{
                        alias: compInfo.sportAlias,
                        name: compInfo.sportName,
                        id: compInfo.sportId
                      }}
                      layoutType={layoutType as EAdvancedEventsLayouts}
                      streamData={streamData}
                      selectedEventIds={selectedEventIds}
                      skeletonCount={10}
                      marketsCountsReduxKey={`${compInfo.compId}${layoutType}`}
                      groupGamesBy={EGroupGamesBy.day}
                    />
                  )
                );
              })}
            </NavigationCollapse>
          )
        );
      }
    )
  ) : !sportsData ? (
    layoutType === ESportsbookLayoutTypes.AMERICAN ? (
      <>
        <div className="favoriteTeamsBody--sportHeader--usa">
          <div className="favoriteTeamsBody--sportHeader--icon">
            <Skeleton.Avatar size={24} active />
          </div>
          <div className="favoriteTeamsBody--sportHeader--name">
            <Skeleton.Button active style={{ minWidth: 160, height: 24 }} />
          </div>
        </div>
        <div className="favoriteTeamsBody--sportHeader--usa">
          <Skeleton.Button active style={{ minWidth: 260, height: 24 }} />
        </div>
        <MatchCardInlineUSASkeleton />
      </>
    ) : (
      <AdvancedGamesFilteredByCompetitionsSkeleton />
    )
  ) : null;
};
